// Протокол Socket.IO между мини-приложением и сервером (docs/spec/02, раздел Realtime).
// Сервер авторитетный: клиент шлёт только свой ввод, исход и счёт присылает сервер.

export const SOCKET_PATH = '/api/socket.io';

export type SideDto = 'A' | 'B';
export type ZoneDto = 'TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR';
export type ShotQualityDto = 'PERFECT' | 'GOOD' | 'BAD';
export type KickOutcomeDto = 'GOAL' | 'SAVE' | 'MISS' | 'POST';
export type FinishReasonDto = 'SCORE' | 'SUDDEN_DEATH' | 'FORFEIT' | 'AFK';

export interface MatchPlayerDto {
  side: SideDto;
  name: string;
  isBot: boolean;
  /** id формы (KITS) */
  kit: string;
  /** мяч и рамка игрока (видны сопернику) */
  ball: string;
  frame: string;
}

export interface KickDto {
  seq: number;
  round: number;
  shooter: SideDto;
  shotZone: ZoneDto;
  keeperZone: ZoneDto;
  quality: ShotQualityDto;
  outcome: KickOutcomeDto;
  autoShot: boolean;
  autoKeeper: boolean;
}

export interface ScoreDto {
  A: number;
  B: number;
}

/** Текущий удар. roundId — ключ, с которым клиент отправляет ход. */
export interface TurnDto {
  matchId: string;
  roundId: string;
  seq: number;
  round: number;
  shooter: SideDto;
  suddenDeath: boolean;
  /** время сервера (мс), до которого принимается ход; дальше — автоход */
  deadline: number;
  serverTime: number;
  /** вы уже сделали ход в этом ударе (после переподключения) */
  submitted: boolean;
}

export interface MatchResultDto {
  matchId: string;
  winner: SideDto;
  reason: FinishReasonDto;
  score: ScoreDto;
  /** изменение рейтинга живых игроков; у бота — null */
  ratingDelta: { A: number | null; B: number | null };
  /** монеты за матч (победа и серия); у бота — null */
  coins: { A: number | null; B: number | null };
}

export type MatchPhaseDto = 'READY' | 'PLAYING' | 'FINISHED';

/** Полное состояние матча: при входе, переподключении и по match:join. */
export interface MatchStateDto {
  matchId: string;
  phase: MatchPhaseDto;
  you: SideDto;
  players: [MatchPlayerDto, MatchPlayerDto];
  score: ScoreDto;
  kicks: KickDto[];
  turn: TurnDto | null;
  result: MatchResultDto | null;
}

export interface RoundResultDto {
  matchId: string;
  roundId: string;
  kick: KickDto;
  score: ScoreDto;
}

export interface ActionAcceptedDto {
  matchId: string;
  roundId: string;
  actionId: string;
  kind: 'shot' | 'keeper';
}

export type GameErrorCode =
  | 'invalid_payload'
  | 'not_in_match'
  | 'match_not_active'
  | 'stale_round'
  | 'not_your_turn'
  | 'already_submitted'
  | 'rate_limited'
  | 'internal';

export interface GameErrorDto {
  code: GameErrorCode;
  actionId?: string;
}

export interface MatchRefDto {
  matchId: string;
}

export interface ShotSubmitDto {
  matchId: string;
  roundId: string;
  actionId: string;
  zone: ZoneDto;
  power: number;
  accuracy: number;
}

export interface KeeperSubmitDto {
  matchId: string;
  roundId: string;
  actionId: string;
  zone: ZoneDto;
}

export interface ServerToClientEvents {
  'match:state': (state: MatchStateDto) => void;
  'match:start': (data: MatchRefDto) => void;
  'round:start': (turn: TurnDto) => void;
  'action:accepted': (data: ActionAcceptedDto) => void;
  'round:result': (data: RoundResultDto) => void;
  'score:update': (data: { matchId: string; score: ScoreDto }) => void;
  'match:finished': (data: MatchResultDto) => void;
  'game:error': (error: GameErrorDto) => void;
  'tournament:update': (tournament: TournamentDto) => void;
  /** друг зовёт в свою дуэль или кубок */
  'invite:incoming': (invite: import('./friends.js').IncomingInviteDto) => void;
  /** друг отклонил ваш вызов */
  'invite:declined': (data: { name: string }) => void;
}

export interface ClientToServerEvents {
  'match:join': (data: MatchRefDto) => void;
  'match:ready': (data: MatchRefDto) => void;
  'match:reconnect': (data: MatchRefDto) => void;
  'shot:submit': (data: ShotSubmitDto) => void;
  'keeper:submit': (data: KeeperSubmitDto) => void;
}

// ---------- турнир ----------

export type TournamentStatusDto = 'WAITING' | 'RUNNING' | 'FINISHED' | 'CANCELLED';
/** CUP — публичный на 4/8 по билету, DUEL — вызов друга 1 на 1, FRIENDS — кубок с друзьями, QUICK — быстрый 1 на 1 */
export type TournamentKindDto = 'CUP' | 'DUEL' | 'FRIENDS' | 'QUICK';
export type MatchStatusDto = 'PENDING' | 'ACTIVE' | 'FINISHED' | 'CANCELLED';

export interface TournamentPlayerDto {
  id: string;
  seed: number;
  name: string;
  isBot: boolean;
  isYou: boolean;
  /** раунд, в котором выбыл; null — ещё в турнире или чемпион */
  eliminatedIn: number | null;
}

export interface BracketMatchDto {
  id: string;
  /** 1 — 1/4, 2 — 1/2, 3 — финал */
  round: number;
  slot: number;
  status: MatchStatusDto;
  playerAId: string | null;
  playerBId: string | null;
  scoreA: number;
  scoreB: number;
  winnerId: string | null;
}

export interface TournamentDto {
  id: string;
  status: TournamentStatusDto;
  kind: TournamentKindDto;
  /** вы создали этот дружеский турнир (можете начать и отменить) */
  isOwner: boolean;
  /** ссылка-приглашение (дружеский турнир, пока набирается) */
  inviteLink: string | null;
  size: number;
  round: number;
  /** когда пустые места займут боты (время сервера, мс); null — турнир уже идёт */
  fillAt: number | null;
  serverTime: number;
  players: TournamentPlayerDto[];
  matches: BracketMatchDto[];
  championId: string | null;
  /** ваш участник в этом турнире */
  youId: string | null;
  /** ваша награда в монетах за место (когда выбыли или турнир окончен) */
  yourReward: number | null;
  /** Gold за кубок (чемпиону публичного турнира): сколько и какого вида */
  yourGold: { amount: number; claimable: boolean } | null;
}

/** GET /api/tournaments/current */
export interface CurrentTournamentResponse {
  tournament: TournamentDto | null;
}
